import React, { useEffect, useMemo, useState } from 'react';
import supplierService from '../services/supplierService';
import inventoryService from '../services/inventoryService';
import returnsToSupplierService from '../services/returnsToSupplierService';
import { useStore } from '../context/StoreContext';
import { useAuth } from '../context/AuthContext';

const REASON_OPTIONS = [
  { value: 'Damaged on delivery', label: 'Damaged on delivery' },
  { value: 'Expired', label: 'Expired' },
  { value: 'Wrong item shipped', label: 'Wrong item shipped' },
  { value: 'Damaged in store', label: 'Damaged in store' },
  { value: 'Other', label: 'Other' },
];

const ReturnsToSupplierPage = () => {
  const { activeStore } = useStore();
  const { user } = useAuth();
  const isAdmin = useMemo(() => user?.roles?.some?.((r) => r.name === 'Admin'), [user?.roles]);

  const [suppliers, setSuppliers] = useState([]);
  const [supplierId, setSupplierId] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [items, setItems] = useState([]);
  const [notes, setNotes] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    if (!activeStore?._id) return;
    supplierService
      .fetchSuppliers({ page: 1, limit: 500, compact: true })
      .then((res) => {
        const list = res?.data || res || [];
        setSuppliers(list);
      })
      .catch(() => setSuppliers([]));
  }, [activeStore?._id]);

  const handleSearch = async () => {
    if (!productSearch.trim() || !activeStore?._id) return;
    setIsSearching(true);
    try {
      const res = await inventoryService.searchProducts(activeStore._id, productSearch.trim());
      setSearchResults(res || []);
    } catch (err) {
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const addItem = (product) => {
    if (!product?._id) return;
    setItems((prev) => {
      const existing = prev.find((p) => p.productId === product._id);
      if (existing) {
        return prev.map((p) => (p.productId === product._id ? { ...p, quantity: p.quantity + 1 } : p));
      }
      return [
        ...prev,
        {
          productId: product._id,
          name: product.name,
          sku: product.sku,
          quantity: 1,
          reason: REASON_OPTIONS[0].value,
        },
      ];
    });
    setProductSearch('');
    setSearchResults([]);
  };

  const updateItem = (productId, field, value) => {
    setItems((prev) =>
      prev.map((p) => (p.productId === productId ? { ...p, [field]: value } : p))
    );
  };

  const removeItem = (productId) => {
    setItems((prev) => prev.filter((p) => p.productId !== productId));
  };

  if (!isAdmin) {
    return <div className="p-6 text-red-600">Only Admins can create returns to supplier.</div>;
  }

  if (!activeStore?._id) {
    return <div className="p-6">Select a store to create a return.</div>;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    if (!supplierId || items.length === 0) {
      setError('Supplier and at least one product are required.');
      return;
    }
    if (items.some((i) => !i.quantity || i.quantity <= 0)) {
      setError('All quantities must be positive.');
      return;
    }
    setIsSubmitting(true);
    try {
      await returnsToSupplierService.createReturn({
        storeId: activeStore._id,
        supplierId,
        invoiceNumber: invoiceNumber || undefined,
        items: items.map((i) => ({
          productId: i.productId,
          quantity: Number(i.quantity),
          reason: i.reason,
        })),
        notes,
      });
      setMessage('Return created and stock decremented.');
      setItems([]);
      setNotes('');
      setInvoiceNumber('');
      setProductSearch('');
      setSearchResults([]);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to create return.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="p-6 max-w-5xl">
      <h1 className="text-2xl font-bold mb-4">Return Damaged Stock to Supplier</h1>
      {message && <div className="bg-green-100 text-green-800 p-3 rounded mb-3">{message}</div>}
      {error && <div className="bg-red-100 text-red-800 p-3 rounded mb-3">{error}</div>}
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Supplier</label>
            <select
              className="w-full border rounded p-2"
              value={supplierId}
              onChange={(e) => setSupplierId(e.target.value)}
            >
              <option value="">Select supplier</option>
              {suppliers.map((s) => (
                <option key={s._id} value={s._id}>{s.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Invoice number (optional)</label>
            <input
              type="text"
              className="w-full border rounded p-2"
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
            />
          </div>
        </div>

        <div className="bg-white border rounded p-3 space-y-2">
          <label className="block text-sm font-semibold text-gray-700 mb-1">Add products by name / SKU / barcode</label>
          <div className="flex gap-2">
            <input
              type="text"
              className="flex-1 border rounded p-2"
              value={productSearch}
              onChange={(e) => setProductSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSearch();
                }
              }}
              placeholder="Scan or type..."
            />
            <button
              type="button"
              onClick={handleSearch}
              className="px-4 py-2 rounded bg-slate-900 text-white"
              disabled={isSearching}
            >
              {isSearching ? 'Searching...' : 'Search'}
            </button>
          </div>
          {searchResults.length > 0 && (
            <div className="max-h-48 overflow-y-auto border rounded p-2 space-y-1">
              {searchResults.map((p) => (
                <button
                  key={p._id}
                  type="button"
                  onClick={() => addItem(p)}
                  className="w-full text-left px-2 py-1 hover:bg-slate-100 rounded"
                >
                  <span className="font-semibold">{p.name}</span>
                  <span className="text-xs text-slate-500 ml-2">SKU: {p.sku || '—'} • Barcode: {p.barcode || '—'}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white border rounded p-3">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold">Items to return</h3>
            <span className="text-xs text-slate-500">Total items: {items.length}</span>
          </div>
          {items.length === 0 ? (
            <p className="text-sm text-slate-500">Add products to create the return.</p>
          ) : (
            <div className="space-y-2">
              {items.map((item) => (
                <div key={item.productId} className="grid grid-cols-1 md:grid-cols-5 gap-2 items-center border rounded p-2">
                  <div className="md:col-span-2">
                    <p className="font-semibold text-sm">{item.name}</p>
                    <p className="text-xs text-slate-500">SKU: {item.sku || '—'}</p>
                  </div>
                  <input
                    type="number"
                    min="1"
                    className="border rounded p-2 w-full"
                    value={item.quantity}
                    onChange={(e) => updateItem(item.productId, 'quantity', Number(e.target.value))}
                  />
                  <select
                    className="border rounded p-2 w-full"
                    value={item.reason}
                    onChange={(e) => updateItem(item.productId, 'reason', e.target.value)}
                  >
                    {REASON_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => removeItem(item.productId)}
                    className="text-rose-600 text-sm"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Notes (optional)</label>
          <textarea
            className="w-full border rounded p-2"
            rows="3"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isSubmitting}
            className="bg-primary text-white px-4 py-2 rounded hover:bg-primary-dark disabled:opacity-50"
          >
            {isSubmitting ? 'Submitting...' : 'Create Return'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ReturnsToSupplierPage;
